import { Button, Typography } from "@/components/atoms";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { ConfirmationDialog, OkDialog } from "@/components/molecules";
import { useIsAdhaJourney, useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
// @ts-ignore Dynatrace service is ts-nocheck; runtime export is available
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { useAppStore } from "@/core/stores/appStore";
import { isSmallScreen } from "@/core/utils/responsive";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { IdProofCardWeb } from "./IdProofCardWeb";
import { ProfileContactCardWeb } from "./ProfileContactCardWeb";
import { useDeleteAccount } from "@/modules/auth/hooks/useDeleteAccount";

function getAccountSecurityPhoneDisplay(
  primaryPhone: string | null | undefined,
  isAdhaJourney: boolean,
): string {
  if (primaryPhone) {
    return `+${primaryPhone}`;
  }
  if (isAdhaJourney) {
    return "—";
  }
  return "";
}

/**
 * Web-only Account & Security screen.
 * Uses WebAppLayout + WebHeader and reuses existing profile data and routes.
 */
export function AccountSecurityScreenWeb() {
  const colors = useUIColors();
  const { t } = useTranslation();
  const isAdhaJourney = useIsAdhaJourney();
  const user = useAppStore((state) => state.auth.user);
  const { isRTL } = useI18nContext();
  const isMobileWeb = isSmallScreen();
  const [deleteAccountDialogVisible, setDeleteAccountDialogVisible] =
    useState(false);
  const { deleteAccount, isLoading: deleteLoading, error: deleteError } =
    useDeleteAccount();
  const [deleteErrorDialogVisible, setDeleteErrorDialogVisible] =
    useState(false);
  const [loadErrorVisible, setLoadErrorVisible] = useState(false);

  const loginEmail =
    user?.primary_email || user?.secondary_email || user?.tertiary_email || "";
  const communicationEmail =
    user?.secondary_email || user?.tertiary_email || loginEmail || "";
  const phoneNumber = user?.primary_phone || "";

  const passportNumber = user?.passport_number || "";
  const emiratesId = user?.emairates_id || "";

  // ADHA: keep fields visible and show "—" when a value is missing.
  // Non-ADHA: unchanged (empty string + edit actions remain available).
  const loginEmailDisplay =
    loginEmail || (isAdhaJourney ? "—" : "");
  const phoneDisplay = getAccountSecurityPhoneDisplay(
    user?.primary_phone,
    isAdhaJourney,
  );

  useEffect(() => {
    trackDynatraceEvent({
      p_screen: "account-security",
      p_actionName: "Account Security Viewed",
      eventType: "view",
    });
  }, []);

  useEffect(() => {
    if (isAdhaJourney && !user) {
      setLoadErrorVisible(true);
    }
  }, [isAdhaJourney, user]);

  // Reuse same edit flows as mobile PersonalDetailsScreen
  const handlePhoneEdit = () => {
    trackDynatraceEvent({
      p_screen: "account-security",
      p_actionName: "Change Phone Number Started",
      eventType: "action",
    });
    const currentPhone = user?.primary_phone || "";
    const countryCode = user?.primary_country_code || "+971";
    let fullPhone = currentPhone;
    if (currentPhone && !currentPhone.startsWith("+")) {
      const codeWithoutPlus = countryCode.replace("+", "");
      fullPhone = `${codeWithoutPlus}${currentPhone}`;
    } else if (!currentPhone) {
      fullPhone = countryCode.replace("+", "");
    }
    router.push({
      pathname: "/(auth)/profile-phone-otp-inline",
      params: {
        contactInfo: fullPhone,
        progress: "0.7",
      },
    } as any);
  };

  const handleEmailEdit = () => {
    trackDynatraceEvent({
      p_screen: "account-security",
      p_actionName: "Change Communication Email Started",
      eventType: "action",
    });
    const currentEmail = user?.secondary_email || user?.primary_email || "";
    router.push({
      pathname: "/(auth)/profile-email-otp-inline",
      params: {
        contactInfo: currentEmail,
        progress: "0.8",
      },
    } as any);
  };

  const handleDeleteAccountPress = () => {
    trackDynatraceEvent({
      p_screen: "account-security",
      p_actionName: "Delete Account Button Pressed",
      eventType: "action",
    });
    setDeleteAccountDialogVisible(true);
  };

  const handleDeleteAccountConfirm = async () => {
    setDeleteAccountDialogVisible(false);

    const success = await deleteAccount();

    if (!success) {
      // Show error dialog only on failure
      // Success case is handled by useDeleteAccount (logout + redirect to welcome)
      setDeleteErrorDialogVisible(true);
    }
  };

  return (
    <WebAppLayout
      activeTab={null}
      headerTitle={isAdhaJourney ? t("profile.personalDetails" as any) : t("profile.accountSecurity")}
      showBackButton={true}
      breadcrumbs={[
        { label: isAdhaJourney ? t("common.home") : t("profile.myProfile"), path: "/(auth)/(home)" },
        { label: isAdhaJourney ? t("profile.personalDetails" as any) : t("profile.accountSecurity") },
      ]}
    >
      <View
        style={[
          styles.pageContainer,
          { backgroundColor: colors.backgroundLevel1 },
        ]}
      >
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={[
            styles.contentWrapper,
            { paddingHorizontal: isMobileWeb ? 16 : 32 },
          ]}
          showsVerticalScrollIndicator={false}
        >
          {/* Personal details */}
          {!isAdhaJourney && (
            <Typography
              variant="title2"
              color="primary"
              style={{ textAlign: isRTL ? "right" : "left" }}
            >
              {t("profile.personalDetails")}
            </Typography>
          )}

          <View style={styles.cardGroup}>
            {/* Login email id */}
            <ProfileContactCardWeb
              title={isAdhaJourney ? t("auth.emailId") : t("profile.loginEmailId" as any)}
              value={
                <View style={{ gap: isMobileWeb ? 4 : 0 }}>
                  <Typography
                    variant="body"
                    color="secondary"
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {loginEmailDisplay}
                  </Typography>
                  {isMobileWeb && isAdhaJourney && (
                    <Typography 
                      variant="caption" 
                      color="tertiary"
                      style={{
                        fontFamily: 'Matter-Medium',
                        fontWeight: '500',
                        fontSize: 12,
                        lineHeight: 12,
                        letterSpacing: 0,
                      }}
                    >
                      {t("profile.loginEmailCannotBeChanged" as any)}
                    </Typography>
                  )}
                </View>
              }
              {...(isAdhaJourney && !isMobileWeb ? { 
                rightElement: (
                  <Typography 
                    variant="caption" 
                    color="tertiary"
                    style={{
                      fontFamily: 'Matter-Medium',
                      fontWeight: '500',
                      fontSize: 12,
                      lineHeight: 12,
                      letterSpacing: 0,
                    }}
                  >
                    {t("profile.loginEmailCannotBeChanged" as any)}
                  </Typography>
                )
              } : {})}
            />

            {/* Phone number */}
            <ProfileContactCardWeb
              title={t("profile.phoneNumber")}
              value={
                <View style={{ direction: "ltr" }}>
                  <Typography
                    variant="body"
                    color="secondary"
                    style={{
                      writingDirection: "ltr",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {phoneDisplay}
                  </Typography>
                </View>
              }
              {...(!isAdhaJourney ? { onEdit: handlePhoneEdit } : {})}
            />

            {/* Communication email id */}
            {!isAdhaJourney && (
              <ProfileContactCardWeb
                title={t("profile.communicationEmailId" as any)}
                value={
                  <Typography
                    variant="body"
                    color="secondary"
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {communicationEmail || ""}
                  </Typography>
                }
                helperText={t("profile.communicationEmailDescription" as any)}
                onEdit={handleEmailEdit}
              />
            )}
          </View>

          {/* ID proofs and other sections */}
          {!isAdhaJourney && (
            <>

          {/* ID proofs */}
          <Typography
            variant="title2"
            color="primary"
            style={{ textAlign: isRTL ? "right" : "left" }}
          >
            {t("profile.idProofs")}
          </Typography>

          <View
            style={[
              styles.row,
              isMobileWeb && { flexDirection: "column" },
              { marginBottom: 16 },
            ]}
          >
            {/* Passport */}
            <IdProofCardWeb
              containerStyle={styles.flex1}
              title={t("profile.passport")}
              value={passportNumber || "-"}
              onView={() =>
                router.push({
                  pathname: "/document-viewer",
                  params: { documentType: "passport" },
                } as any)
              }
            />

            {/* Emirates ID */}
            <IdProofCardWeb    
              containerStyle={styles.flex1}
              title={t("profile.emiratesId")}
              value={emiratesId || "-"}
              onView={() =>
                router.push({
                  pathname: "/document-viewer",
                  params: { documentType: "emiratesIdFront" },
                } as any)
              }
            />
          </View>

          {/* Change password */}
          <View
            style={[
              styles.changePasswordContainer,
              isMobileWeb && styles.changePasswordContainerMobile,
            ]}
          >
            <View style={[styles.card, !isRTL && { gap: 8 }]}>
              <Typography
                variant="title2"
                color="primary"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("profile.privacyChangePassword")}
              </Typography>
              <Typography
                variant="body"
                color="tertiary"
                style={[
                  styles.helperText,
                  { textAlign: isRTL ? "right" : "left" },
                ]}
              >
                {t("profile.changePasswordDescription" as any)}
              </Typography>
            </View>
            <Button
              title={t("profile.privacyChangePassword")}
              variant="outline"
              onPress={() => {
                trackDynatraceEvent({
                  p_screen: "account-security",
                  p_actionName: "Password Change Started",
                  eventType: "action",
                });
                router.push("/change-password?from=profile" as any);
              }}
              fullWidth={isMobileWeb}
            />
          </View>

          {/* Delete account */}
          <View
            style={[
              styles.changePasswordContainer,
              isMobileWeb && styles.changePasswordContainerMobile,
            ]}
          >
            <View style={[styles.card, !isRTL && { gap: 8 }]}>
              <Typography
                variant="title2"
                color="primary"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("profile.deactivate" as any)}
              </Typography>
              <Typography
                variant="body"
                color="tertiary"
                style={[
                  styles.helperText,
                  { textAlign: isRTL ? "right" : "left" },
                ]}
              >
                {t("profile.deactivateDescription" as any)}
              </Typography>
            </View>
            <Button
              title={t("profile.deactivateAccount" as any)}
              variant="outline"
              onPress={handleDeleteAccountPress}
              fullWidth={isMobileWeb}
              disabled={deleteLoading}
            />
          </View>
          </>
          )}
        </ScrollView>
      </View>

      <ConfirmationDialog
        visible={deleteAccountDialogVisible}
        message={t("profile.deactivateConfirmationTitle" as any)}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={handleDeleteAccountConfirm}
        onCancel={() => setDeleteAccountDialogVisible(false)}
      />

      <OkDialog
        visible={deleteErrorDialogVisible}
        message={deleteError || t("common.error")}
        onOk={() => {
          setDeleteErrorDialogVisible(false);
        }}
      />

      <OkDialog
        visible={loadErrorVisible}
        message={t("profile.personalDetailsLoadError")}
        onOk={() => setLoadErrorVisible(false)}
      />
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    borderRadius: 12,
  },
  scroll: {
    flex: 1,
  },
  contentWrapper: {
    paddingHorizontal: 32,
    paddingBottom: 32,
    paddingTop: 24,
    gap: 12,
  },
  mobileTitle: {
    marginBottom: 16,
    fontSize: 24,
    lineHeight: 32,
    fontWeight: "600",
  },
  cardGroup: {
    gap: 16,
    marginBottom: 16,
  },
  card: {
    flex: 1,
    gap: 0,
  },
  cardLabel: {
    marginBottom: 0,
  },
  helperText: {
    marginTop: 0,
    marginBottom: 0,
  },
  row: {
    flexDirection: "row",
    gap: 16,
  },
  flex1: {
    flex: 1,
  },
  inputLike: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  actionsRow: {
    marginTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  changePasswordContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  changePasswordContainerMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 16,
  },
  changePasswordLabelWrap: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    alignSelf: "stretch",
  },
  changePasswordLabelWrapMobile: {
    flex: undefined,
  },
});

export default AccountSecurityScreenWeb;
